import { cn } from "@/utils";
import { Icon } from "@/components/Icons";
import { INavItem } from "@/types";

/**
 * Navigation button props
 */
interface NavButtonProps {
  /** Navigation item data */
  item: INavItem;
  /** Whether the navigation button is expanded */
  expanded: boolean;
  /** Callback function to handle click events on the navigation button */
  onClick?: () => void;
}

/**
 * Navigation button component that renders individual navigation items
 * with appropriate styling and behavior.
 */
export const NavButton: React.FC<NavButtonProps> = ({ item, expanded, onClick }) => {
  const buttonClasses = cn(
    'flex items-center gap-3 rounded-lg p-3 transition-all duration-200',
    'focus-visible:ring-2 focus-visible:ring-neutral-white focus-visible:ring-offset-2',
    'hover:bg-white/10 focus:bg-white/10 outline-none',
    {
      'bg-white/20': item.isActive,
      'w-full justify-center': !expanded,
      'w-full': expanded,
    },
  );

  /** Icon classes for navigation button */
  const iconClasses = cn('shrink-0 pointer-events-none');

  /** Text classes for navigation button */
  const textClasses = cn(
    'font-text text-body font-medium',
    'text-white whitespace-nowrap pointer-events-none',
  );

  /**
   * Handle click event on navigation item
   * @todo why blur no workies
   */
  const handleOnClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (onClick) {
      onClick();
      e.currentTarget.blur(); // Remove focus after click for better UX
    }
  };

  //=============================================================
  // Render
  //============================================================= 
  return (
    <button
      type="button"
      className={buttonClasses}
      onClick={handleOnClick}
      aria-current={item.isActive ? 'page' : undefined}
      aria-label={item.label}
      title={item.label}
    >
      <Icon name={item.icon} size={24} color="white" className={iconClasses} />
      {expanded && <span className={textClasses}>{item.label}</span>}
    </button>
  );
};

NavButton.displayName = 'NavButton';

export default NavButton;
